import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  SwipeableDrawer,
  useMediaQuery,
  useTheme,
  Avatar,
  Divider,
  keyframes,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SendIcon from "@mui/icons-material/Send";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AddIcon from "@mui/icons-material/Add";
import HistoryIcon from "@mui/icons-material/History";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useNavigate } from "react-router-dom";
import { useAssistantChat } from "./AssistantChatContext";
import axiosInstance from "../../utils/axiosInstance";
import { logUserAction } from "../../utils/analytics";
import { motion } from "framer-motion";

const MAX_HISTORY_MESSAGES = 4;

const pulseAnimation = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 140, 56, 0.4); }
  70% { transform: scale(1.05); box-shadow: 0 0 0 6px rgba(255, 140, 56, 0); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 140, 56, 0); }
`;

const AssistantChatPopup = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    isOpen,
    chatThreads,
    activeChatThreadId,
    activeThread,
    isLoading,
    setIsLoading,
    closeChat,
    addMessage,
    createNewChatThread,
    switchChatThread,
    deleteThread,
    loadMoreHistory,
    hasMore,
    isFetchingHistory
  } = useAssistantChat();

  const [inputValue, setInputValue] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, threadId: null });
  const [keyboardOffset, setKeyboardOffset] = useState(0);
  const [fixedHeight, setFixedHeight] = useState("85dvh");
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Handle mobile keyboard overlapping the fixed drawer
  useEffect(() => {
    if (!isMobile) return;

    const handleResize = () => {
      // Capture the maximum layout height (ignores keyboard shrinks)
      if (!window.maxLayoutHeight || window.innerHeight > window.maxLayoutHeight) {
        window.maxLayoutHeight = window.innerHeight;
        setFixedHeight(`${window.innerHeight * 0.85}px`);
      }
    };
    window.addEventListener("resize", handleResize);
    handleResize();

    if (!window.visualViewport) return;

    const handleViewportChange = () => {
      const offsetTop = window.visualViewport.offsetTop;
      const visualHeight = window.visualViewport.height;
      const layoutHeight = window.innerHeight;
      
      // Calculate how much of the layout viewport is obscured at the bottom
      let hiddenBottom = layoutHeight - (offsetTop + visualHeight);
      if (hiddenBottom < 0) hiddenBottom = 0;
      
      setKeyboardOffset(hiddenBottom);
    };

    window.visualViewport.addEventListener("resize", handleViewportChange);
    window.visualViewport.addEventListener("scroll", handleViewportChange);
    handleViewportChange();

    return () => {
      window.removeEventListener("resize", handleResize);
      window.visualViewport.removeEventListener("resize", handleViewportChange);
      window.visualViewport.removeEventListener("scroll", handleViewportChange);
    };
  }, [isMobile]);

  // Reset view when chat opens or switches thread
  useEffect(() => {
    setShowHistory(false);
  }, [isOpen, activeChatThreadId]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (!showHistory && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [activeThread?.messages, isLoading, showHistory]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !showHistory && inputRef.current && !isMobile) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen, showHistory, isMobile]);

  const handleSend = async (overrideQuery = null) => {
    const textToSubmit = typeof overrideQuery === "string" ? overrideQuery : inputValue;
    const trimmed = textToSubmit.trim();
    if (!trimmed || isLoading) return;

    addMessage("user", trimmed);
    if (trimmed === inputValue.trim()) setInputValue("");
    setIsLoading(true);

    try {
      // The current messages state for this thread (before we just added the user message, so we grab it from context but add our new one)
      const currentMessages = activeThread?.messages || [];
      const recentHistory = [...currentMessages, { role: "user", text: trimmed }]
        .map(m => ({ role: m.role, text: m.text }))
        .slice(-MAX_HISTORY_MESSAGES);

      const token = localStorage.getItem("userToken");
      const response = await axiosInstance.post(
        "/assistant/chat",
        {
          query: trimmed,
          product_url: activeThread?.productUrl || null,
          original_query: activeThread?.originalQuery || null,
          history: recentHistory,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = response.data;

      let products = null;
      if (data.intent === "search" && data.products && data.products.length > 0) {
        products = data.products;
        
        // Log brand impressions for top 5
        const top5 = products.slice(0, 5);
        const uniqueBrands = [...new Set(top5.map(p => p.brand).filter(Boolean))];
        if (uniqueBrands.length > 0) {
           import("../../utils/analytics").then(({ logBatchUserAction }) => {
              logBatchUserAction(uniqueBrands.map(brand => ({
                action: "brand_impression",
                data: { brand: brand, source: "AI Assistant" }
              })));
           });
        }
      }

      addMessage("assistant", data.text_response || "I couldn't generate a response.", products, data.search_query || null);
      
      // Log permanent interaction to DB
      logUserAction({
         action: "assistant_interaction",
         data: {
           chat_thread_id: activeThread?.id,
           user_message: trimmed,
           ai_response: data.text_response || "I couldn't generate a response.",
           ai_search_query: data.search_query || null,
           returned_products: products ? products.length : 0,
           product_url: activeThread?.productUrl || null,
           product_info: activeThread?.productInfo || null,
         }
      });

    } catch (err) {
      console.error("Assistant chat error:", err);
      const errMsg =
        err?.response?.status === 401
          ? "Your session has expired. Please log in again."
          : "Something went wrong. Please try again.";
      addMessage("assistant", errMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleViewAllResults = (products) => {
    logUserAction({
      action: "assistant_results_viewed",
      data: { count: products.length, chat_thread_id: activeThread?.id }
    });

    // Log remaining brand impressions
    if (products.length > 5) {
      const top5Brands = new Set(products.slice(0, 5).map(p => p.brand).filter(Boolean));
      const remaining = products.slice(5);
      const uniqueRemainingBrands = [...new Set(remaining.map(p => p.brand).filter(Boolean))].filter(b => !top5Brands.has(b));
      if (uniqueRemainingBrands.length > 0) {
        import("../../utils/analytics").then(({ logBatchUserAction }) => {
          logBatchUserAction(uniqueRemainingBrands.map(brand => ({
            action: "brand_impression",
            data: { brand: brand, source: "AI Assistant" }
          })));
        });
      }
    }

    navigate("/assistant-results", {
      state: { products, trackingContext: { source: "AI Assistant" } },
    });
    closeChat();
  };

  const formatDate = (ts) => {
    const d = new Date(ts);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <>
      <SwipeableDrawer
        anchor={isMobile ? "bottom" : "right"}
        open={isOpen}
        onClose={closeChat}
        onOpen={() => { }} // SwipeableDrawer requires onOpen, though we open it externally via context
        PaperProps={{
          sx: {
            width: isMobile ? "100%" : "25vw",
            minWidth: isMobile ? "100%" : 350,
            maxHeight: isMobile ? "none" : "100dvh",
            height: isMobile ? fixedHeight : "100dvh",
            marginBottom: isMobile ? `${keyboardOffset}px` : 0,
            borderTopLeftRadius: isMobile ? 24 : 0,
            borderTopRightRadius: isMobile ? 24 : 0,
            background: "#FAFAFA",
            display: "flex",
            flexDirection: "column",
            transition: "margin-bottom 0.1s ease-out, height 0.1s ease-out, max-height 0.1s ease-out",
          }
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #FF8C38 0%, #FF9B80 100%)",
            px: 2,
            pb: 2,
            pt: isMobile ? 3.5 : 2,
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 1,
            boxShadow: "0 4px 20px rgba(255, 140, 56, 0.2)",
            zIndex: 2,
          }}
        >
          {/* Drawer Handle for Mobile */}
          {isMobile && (
            <Box sx={{ position: "absolute", top: 12, left: "50%", transform: "translateX(-50%)", width: 40, height: 4, bgcolor: "rgba(255,255,255,0.3)", borderRadius: 2 }} />
          )}
          <Avatar
            src={require("../../assets/images/login-handpik-logo_cropped.png")}
            sx={{
              width: 32,
              height: 32,
              bgcolor: "transparent",
              img: { objectFit: "contain", filter: "brightness(0) invert(1)" },
              animation: `${pulseAnimation} 2.5s infinite`
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: "Poppins-SemiBold, sans-serif",
                fontSize: "15px",
                color: "#FFF",
                letterSpacing: "0.5px",
              }}
            >
              HANDPIK AI
            </Typography>
          </Box>

          {/* Header Actions */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
            <IconButton
              onClick={() => {
                setShowHistory(false);
                createNewChatThread();
              }}
              size="small"
              sx={{ color: "#FFF", bgcolor: "rgba(255,255,255,0.15)", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" } }}
              title="New Chat"
            >
              <AddIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={() => setShowHistory(!showHistory)}
              size="small"
              sx={{ color: "#FFF", bgcolor: showHistory ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0.15)", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" } }}
              title="Chat History"
            >
              <HistoryIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={closeChat} size="small" sx={{ color: "#FFF", bgcolor: "rgba(255,255,255,0.15)", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* History View */}
        {showHistory && (
          <Box
            sx={{ flex: 1, overflowY: "auto", p: 2, display: "flex", flexDirection: "column", gap: 1 }}
            onScroll={(e) => {
              const bottom = e.target.scrollHeight - e.target.scrollTop - e.target.clientHeight < 5;
              if (bottom && hasMore) {
                loadMoreHistory();
              }
            }}
          >
            <Typography sx={{ fontFamily: "Poppins-Medium, sans-serif", fontSize: "14px", color: "#666", mb: 1, px: 1 }}>
              Recent Conversations
            </Typography>

            {chatThreads.filter(t => !t.isTemp).length === 0 ? (
              <Typography sx={{ fontFamily: "Poppins-Regular, sans-serif", fontSize: "13px", color: "#999", px: 1 }}>
                No saved conversations yet.
              </Typography>
            ) : (
              [...chatThreads].filter(t => !t.isTemp).sort((a, b) => b.updatedAt - a.updatedAt).map(thread => (
                <Box
                  key={thread.id}
                  onClick={() => {
                    switchChatThread(thread.id);
                    setShowHistory(false);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: "12px",
                    bgcolor: thread.id === activeChatThreadId ? "rgba(255, 140, 56, 0.08)" : "#FFF",
                    border: "1px solid",
                    borderColor: thread.id === activeChatThreadId ? "rgba(255, 140, 56, 0.2)" : "rgba(0,0,0,0.06)",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": {
                      bgcolor: "rgba(255, 140, 56, 0.05)",
                      borderColor: "rgba(255, 140, 56, 0.3)",
                    }
                  }}
                >
                  {thread.productInfo ? (
                    <Box
                      component="img"
                      src={thread.productInfo.image_url}
                      sx={{ width: 44, height: 44, borderRadius: "8px", objectFit: "cover", bgcolor: "#F5F5F5" }}
                    />
                  ) : (
                    <Avatar
                      src={require("../../assets/images/login-handpik-logo_cropped.png")}
                      sx={{
                        width: 44,
                        height: 44,
                        bgcolor: "rgba(255, 140, 56, 0.1)",
                        border: "1px solid rgba(255, 140, 56, 0.3)",
                        img: { objectFit: "contain", p: 0.5 }
                      }}
                    />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontFamily: "Poppins-Medium, sans-serif", fontSize: "13px", color: "#1F1F1F", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {thread.messages?.[0]?.text || "New Chat"}
                    </Typography>
                    <Typography sx={{ fontFamily: "Poppins-Regular, sans-serif", fontSize: "11px", color: "#888" }}>
                      {formatDate(thread.updatedAt)}
                    </Typography>
                  </Box>

                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteDialog({ open: true, threadId: thread.id });
                    }}
                    sx={{ color: "#999", "&:hover": { color: "#D32F2F", bgcolor: "rgba(211, 47, 47, 0.08)" } }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                  <ChevronRightIcon sx={{ color: "#CCC", fontSize: 20 }} />
                </Box>
              ))
            )}

            {/* Pagination Loading Indicator */}
            {isFetchingHistory && chatThreads.length > 0 && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={24} sx={{ color: "#FF8C38" }} />
              </Box>
            )}
          </Box>
        )}

        {/* Messages Area */}
        {!showHistory && (
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              px: 2,
              py: 2,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              "&::-webkit-scrollbar": { width: 6 },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(0,0,0,0.1)",
                borderRadius: 3,
              },
            }}
          >
            {/* Welcome / Empty state */}
            {(!activeThread?.messages || activeThread.messages.length === 0) && !isLoading && (
              <Box sx={{ textAlign: "center", py: 5, px: 2 }}>
                <Avatar
                  src={require("../../assets/images/login-handpik-logo_cropped.png")}
                  sx={{
                    width: 56,
                    height: 56,
                    bgcolor: "rgba(255, 140, 56, 0.1)",
                    border: "1px solid rgba(255, 140, 56, 0.3)",
                    margin: "0 auto",
                    mb: 2,
                    img: { objectFit: "contain", p: 1 },
                    animation: `${pulseAnimation} 2.5s infinite`
                  }}
                />
                <Typography sx={{ fontFamily: "Poppins-SemiBold, sans-serif", fontSize: "16px", color: "#1F1F1F", mb: 1 }}>
                  How can I style you today?
                </Typography>

                {activeThread?.productInfo ? (
                  <Box sx={{ mt: 3, p: 2, bgcolor: "#FFF", borderRadius: "16px", boxShadow: "0 4px 16px rgba(0,0,0,0.06)", border: "1px solid rgba(0,0,0,0.04)" }}>
                    <Typography sx={{ fontFamily: "Poppins-Medium, sans-serif", fontSize: "12px", color: "#666", mb: 1.5 }}>
                      Refine search or find similar to:
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, textAlign: "left" }}>
                      <Box component="img" src={activeThread.productInfo.image_url} sx={{ width: 80, height: 104, borderRadius: "8px", objectFit: "cover" }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontFamily: "Poppins-Regular, sans-serif", fontSize: "12px", color: "#333", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                          {activeThread.productInfo.title}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography sx={{ fontFamily: "Poppins-Medium, sans-serif", fontSize: "11px", color: "#888", mt: 2, mb: 1, textAlign: "center" }}>
                      Try asking:
                    </Typography>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.25, ease: "easeOut" }}
                      style={{ width: "100%", display: "flex", flexDirection: "column", gap: "8px" }}
                    >
                      <Box sx={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                        {["Find more in this style", "Show cheaper options"].map((q, idx) => (
                          <Box
                            key={idx}
                            onClick={() => handleSend(q)}
                            sx={{
                              background: "rgba(125, 211, 224, 0.08)",
                              border: "1px solid rgba(125, 211, 224, 0.25)",
                              color: "#5abccb",
                              px: 1.5,
                              py: 0.75,
                              borderRadius: "100px",
                              fontSize: "11px",
                              fontFamily: "Poppins-Medium",
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                background: "rgba(125, 211, 224, 0.15)",
                                transform: "translateY(-1px)"
                              },
                              "&:active": {
                                transform: "scale(0.97)"
                              }
                            }}
                          >
                            "{q}"
                          </Box>
                        ))}
                      </Box>
                      <Box sx={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                        {["Find me trousers to pair with this"].map((q, idx) => (
                          <Box
                            key={idx}
                            onClick={() => handleSend(q)}
                            sx={{
                              background: "rgba(125, 211, 224, 0.08)",
                              border: "1px solid rgba(125, 211, 224, 0.25)",
                              color: "#5abccb",
                              px: 1.5,
                              py: 0.75,
                              borderRadius: "100px",
                              fontSize: "11px",
                              fontFamily: "Poppins-Medium",
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                background: "rgba(125, 211, 224, 0.15)",
                                transform: "translateY(-1px)"
                              },
                              "&:active": {
                                transform: "scale(0.97)"
                              }
                            }}
                          >
                            "{q}"
                          </Box>
                        ))}
                      </Box>
                    </motion.div>
                  </Box>
                ) : (
                  <Typography sx={{ fontFamily: "Poppins-Regular, sans-serif", fontSize: "13px", color: "#666", lineHeight: 1.6 }}>
                    I can help you find outfits, suggest trends, or refine your search results.
                  </Typography>
                )}
              </Box>
            )}

            {activeThread?.messages?.map((msg, idx) => {
              // Determine if this is the first user message in a thread that has a product context
              const isFirstUserMessage = idx === 0 && msg.role === "user" && activeThread.productInfo;

              return (
                <Box key={idx} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {/* Text Bubble */}
                  <Box sx={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start", width: "100%" }}>
                    {msg.role === "assistant" && (
                      <Avatar
                        src={require("../../assets/images/login-handpik-logo_cropped.png")}
                        sx={{
                          width: 28,
                          height: 28,
                          mr: 1,
                          mt: 0.5,
                          background: "linear-gradient(135deg, #FF8C38 0%, #FF9B80 100%)",
                          boxShadow: "0 2px 8px rgba(255, 140, 56, 0.2)",
                          img: { objectFit: "contain", filter: "brightness(0) invert(1)", transform: "scale(0.65)" },
                        }}
                      />
                    )}
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: msg.role === "user" ? "flex-end" : "flex-start", maxWidth: msg.role === "assistant" ? "calc(85% - 36px)" : "85%" }}>

                      {/* Inline Product Context before user message */}
                      {isFirstUserMessage && (
                        <Box sx={{
                          display: "flex", alignItems: "center", gap: 1,
                          p: 1, mb: 1,
                          bgcolor: "rgba(255, 140, 56, 0.06)",
                          borderRadius: "10px",
                          border: "1px solid rgba(255, 140, 56, 0.2)",
                          width: "100%"
                        }}>
                          <Box component="img" src={activeThread.productInfo.image_url} sx={{ width: 56, height: 72, borderRadius: "6px", objectFit: "cover" }} />
                          <Typography sx={{ fontFamily: "Poppins-Medium, sans-serif", fontSize: "12px", color: "#333", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: 1 }}>
                            {activeThread.productInfo.title}
                          </Typography>
                        </Box>
                      )}

                      <Box
                        sx={{
                          px: 2,
                          py: 1.25,
                          borderRadius:
                            msg.role === "user"
                              ? "16px 16px 4px 16px"
                              : "16px 16px 16px 4px",
                          background:
                            msg.role === "user"
                              ? "linear-gradient(135deg, #FF8C38 0%, #FF9B80 100%)"
                              : "#FFF",
                          color: msg.role === "user" ? "#FFF" : "#1F1F1F",
                          boxShadow:
                            msg.role === "user"
                              ? "0 4px 12px rgba(255, 140, 56, 0.2)"
                              : "0 2px 10px rgba(0,0,0,0.05)",
                          border: msg.role === "assistant" ? "1px solid rgba(0,0,0,0.04)" : "none",
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "Poppins-Regular, sans-serif",
                            fontSize: "13.5px",
                            lineHeight: 1.6,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {msg.text}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Inline Product Results */}
                  {msg.products && msg.products.length > 0 && (
                    <Box sx={{ mt: 0.5, mb: 1, ml: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1.25,
                          overflowX: "auto",
                          pb: 1.5,
                          pt: 0.5,
                          "&::-webkit-scrollbar": { height: 4 },
                          "&::-webkit-scrollbar-thumb": { background: "rgba(0,0,0,0.12)", borderRadius: 2 },
                        }}
                      >
                        {msg.products.slice(0, 5).map((p, i) => (
                          <Box
                            key={i}
                            onClick={() => {
                              logUserAction({
                                action: "product_click",
                                data: {
                                  product_id: p.product_id || null,
                                  product_title: p.title || null,
                                  brand: p.brand || null,
                                  product_price: p.price?.discounted || p.price?.original || null,
                                  currency: p.price?.currency || null,
                                  product_image: p.image_url || p.images?.[0] || null,
                                  product_url: p.url || null,
                                  source: "AI Assistant",
                                },
                              });
                              navigate("/product", { state: { product: p, similarProducts: [p], trackingContext: { source: "AI Assistant" } } });
                            }}
                            sx={{
                              minWidth: 110,
                              maxWidth: 110,
                              borderRadius: "12px",
                              overflow: "hidden",
                              cursor: "pointer",
                              boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
                              transition: "all 0.2s ease",
                              flexShrink: 0,
                              backgroundColor: "#FFF",
                              border: "1px solid rgba(0,0,0,0.04)",
                              "&:hover": { transform: "translateY(-3px)", boxShadow: "0 6px 16px rgba(0,0,0,0.1)" },
                            }}
                          >
                            <Box component="img" src={p.image_url || p.images?.[0]} sx={{ width: "100%", height: 125, objectFit: "cover" }} />
                            <Box sx={{ p: 1 }}>
                              <Typography sx={{ fontFamily: "Poppins-Medium, sans-serif", fontSize: "10.5px", display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical", overflow: "hidden", color: "#333", mb: 0.5 }}>
                                {p.title}
                              </Typography>
                              {p.price && (
                                <Typography sx={{ fontFamily: "Poppins-SemiBold, sans-serif", fontSize: "11px", color: "#FF8C38" }}>
                                  {p.price.currency} {p.price.discounted || p.price.original}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Box>

                      {msg.products.length > 5 && (
                        <Box
                          onClick={() => handleViewAllResults(msg.products)}
                          sx={{
                            mt: 0.5, mr: 1, py: 1, textAlign: "center", borderRadius: "10px", cursor: "pointer",
                            bgcolor: "rgba(255, 140, 56, 0.08)", color: "#FF8C38", transition: "all 0.2s",
                            "&:hover": { bgcolor: "rgba(255, 140, 56, 0.15)" },
                          }}
                        >
                          <Typography sx={{ fontFamily: "Poppins-SemiBold, sans-serif", fontSize: "12px" }}>
                            View All {msg.products.length} Results
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              );
            })}

            {/* Loading indicator */}
            {isLoading && (
              <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 1 }}>
                <Box sx={{ px: 2, py: 1.5, borderRadius: "16px 16px 16px 4px", background: "#FFF", boxShadow: "0 2px 10px rgba(0,0,0,0.05)", display: "flex", alignItems: "center", gap: 1.5 }}>
                  <CircularProgress size={16} sx={{ color: "#FF8C38" }} />
                  <Typography sx={{ fontFamily: "Poppins-Regular, sans-serif", fontSize: "13px", color: "#666" }}>
                    Finding the best matches...
                  </Typography>
                </Box>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>
        )}

        {/* Input Area */}
        {!showHistory && (
          <Box sx={{ p: 2, bgcolor: "#FFF", borderTop: "1px solid rgba(0,0,0,0.08)", boxShadow: "0 -4px 20px rgba(0,0,0,0.03)" }}>
            <TextField
              inputRef={inputRef}
              fullWidth
              placeholder={activeThread?.productInfo ? "Search related to this item..." : "Search for fashion..."}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={(e) => {
                if (isMobile) {
                  setTimeout(() => {
                    e.target.scrollIntoView({ behavior: "smooth", block: "end" });
                    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
                  }, 300);
                }
              }}
              onClick={(e) => {
                if (isMobile) {
                  setTimeout(() => {
                    e.target.scrollIntoView({ behavior: "smooth", block: "end" });
                  }, 300);
                }
              }}
              disabled={isLoading}
              multiline
              maxRows={3}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "24px",
                  fontFamily: "Poppins-Regular, sans-serif",
                  fontSize: isMobile ? "16px" : "14px",
                  backgroundColor: "#F5F6F8",
                  pr: 1,
                  "& fieldset": { border: "1px solid transparent" },
                  "&:hover fieldset": { border: "1px solid rgba(0,0,0,0.1)" },
                  "&.Mui-focused fieldset": { border: "1px solid #FF8C38" },
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleSend}
                      disabled={!inputValue.trim() || isLoading}
                      sx={{
                        bgcolor: inputValue.trim() && !isLoading ? "#FF8C38" : "transparent",
                        color: inputValue.trim() && !isLoading ? "#FFF" : "rgba(0,0,0,0.3)",
                        width: 36,
                        height: 36,
                        transition: "all 0.2s",
                        "&:hover": {
                          bgcolor: inputValue.trim() && !isLoading ? "#E67A2E" : "transparent",
                        },
                        "&.Mui-disabled": {
                          bgcolor: "transparent",
                          color: "rgba(0,0,0,0.2)",
                        }
                      }}
                    >
                      <SendIcon sx={{ fontSize: 18, ml: inputValue.trim() && !isLoading ? 0.3 : 0 }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}
      </SwipeableDrawer>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, threadId: null })}
        PaperProps={{
          sx: { borderRadius: "16px", p: 1 }
        }}
      >
        <DialogTitle sx={{ fontFamily: "Poppins-SemiBold", fontSize: "18px", color: "#1F1F1F" }}>
          Delete Conversation?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: "Poppins-Regular", fontSize: "14px", color: "#666" }}>
            Are you sure you want to delete this chat? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteDialog({ open: false, threadId: null })}
            sx={{ fontFamily: "Poppins-Medium", color: "#666" }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              deleteThread(deleteDialog.threadId);
              setDeleteDialog({ open: false, threadId: null });
            }}
            variant="contained"
            sx={{
              fontFamily: "Poppins-Medium",
              bgcolor: "#D32F2F",
              borderRadius: "8px",
              "&:hover": { bgcolor: "#B71C1C" }
            }}
            disableElevation
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AssistantChatPopup;
